import { z } from "zod";
import { WorkflowStatus } from "./WorkflowSchemas.ts";
import {
  createGetListRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelSchema,
} from "../util/index.ts";

/** Investigation lifecycle values returned by Carbon Hub API. */
export const InvestigationStatus = {
  PENDING: "pending",
  RUNNING: "running",
  /** Agent turn finished; an approved tool (e.g. workflow) is still executing. */
  AWAITING_EXECUTION: "awaiting_execution",
  DONE: "done",
  ERROR: "error",
} as const;
export type InvestigationStatusType =
  (typeof InvestigationStatus)[keyof typeof InvestigationStatus];

export const TERMINAL_INVESTIGATION_STATUSES = [
  InvestigationStatus.DONE,
  InvestigationStatus.ERROR,
] as const satisfies readonly InvestigationStatusType[];

export const InvestigationMessageRole = {
  USER: "user",
  AGENT: "agent",
} as const;
export type InvestigationMessageRoleType =
  (typeof InvestigationMessageRole)[keyof typeof InvestigationMessageRole];

/** Investigation message categories emitted by Carbon Hub backend. */
export const InvestigationMessageKind = {
  MESSAGE: "message",
  ARTIFACT: "artifact",
  PENDING_APPROVAL: "pending_approval",
  APPROVAL_DECISION: "approval_decision",
} as const;
export type InvestigationMessageKindType =
  (typeof InvestigationMessageKind)[keyof typeof InvestigationMessageKind];

/** Operator decisions accepted when resuming an interrupted investigation turn. */
export const ApprovalDecision = {
  APPROVE: "approve",
  REJECT: "reject",
  ABANDON: "abandon",
} as const;
export type ApprovalDecisionType =
  (typeof ApprovalDecision)[keyof typeof ApprovalDecision];

/** Metadata payload persisted on `approval_decision` messages. */
export const ApprovalDecisionMetadataSchema = z
  .object({
    decision: z.nativeEnum(ApprovalDecision),
  })
  .passthrough();
export type ApprovalDecisionMetadataType = z.infer<
  typeof ApprovalDecisionMetadataSchema
>;

/**
 * Progress event emitted by the investigation agent (NDJSON / stored JSON).
 * `v` and `ts` are wire-protocol names from the agent; see follow-up to rename.
 *
 * `.passthrough()` preserves agent-supplied debug/meta keys at runtime so the UI
 * can surface them in the "Show details" disclosure of the progress feed without
 * us needing to enumerate every possible field here.
 */
export const StepInvestigationProgressEventSchema = z
  .object({
    /** Event protocol version emitted by the investigation agent. */
    v: z.literal(1),
    type: z.literal("step"),
    /** ISO-8601 event timestamp from the agent. */
    ts: z.string().datetime(),
    phase: z.string().min(1),
    detail: z.string(),
  })
  .passthrough();
export type StepInvestigationProgressEventType = z.infer<
  typeof StepInvestigationProgressEventSchema
> &
  Record<string, unknown>;

export const InvestigationProgressEventSchema = z.discriminatedUnion("type", [
  StepInvestigationProgressEventSchema,
]);
export type InvestigationProgressEventType = z.infer<
  typeof InvestigationProgressEventSchema
>;

export const InvestigationErrorSchema = z
  .object({
    message: z.string().min(1),
  })
  .passthrough();
export type InvestigationErrorType = z.infer<typeof InvestigationErrorSchema>;

/** Known `tool_name` values the investigation UI has dedicated approval bodies for. */
export const PendingApprovalToolName = {
  CALL_WRITE_API: "call_write_api",
  EXECUTE_WORKFLOW: "execute_workflow",
} as const;
export type PendingApprovalToolNameType =
  (typeof PendingApprovalToolName)[keyof typeof PendingApprovalToolName];

/**
 * Agent payload for `pending_approval` messages (HITL envelope).
 */
export const PendingApprovalMetadataSchema = z
  .object({
    tool_name: z.string().min(1),
    tool_metadata: z.record(z.unknown()),
  })
  .passthrough();
export type PendingApprovalMetadataType = z.infer<
  typeof PendingApprovalMetadataSchema
>;

/** Args of the investigation agent's single-call write tool. */
export const CallWriteApiMetadataSchema = z
  .object({
    api: z.string().min(1),
    method: z.string().min(1),
    path: z.string().min(1),
    params: z.record(z.unknown()).nullish(),
    body: z.unknown().optional(),
  })
  .passthrough();
export type CallWriteApiMetadataType = z.infer<
  typeof CallWriteApiMetadataSchema
>;

/** Args of the investigation agent's execute_workflow tool (pending approval / in-progress). */
export const ExecuteWorkflowMetadataSchema = z
  .object({
    name: z.string().min(1),
    category_name: z.string().min(1),
    config: z.record(z.unknown()),
    start_input: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough();
export type ExecuteWorkflowMetadataType = z.infer<
  typeof ExecuteWorkflowMetadataSchema
>;

/** Terminal workflow outcome on investigation agent messages after EventBridge completion/failed. */
export const ExecuteWorkflowToolResultStatusSchema = z.enum([
  WorkflowStatus.COMPLETE,
  WorkflowStatus.ERROR,
]);
export type ExecuteWorkflowToolResultStatusType = z.infer<
  typeof ExecuteWorkflowToolResultStatusSchema
>;

export const ExecuteWorkflowToolResultSchema = z
  .object({
    workflow_id: z.string().uuid(),
    status: ExecuteWorkflowToolResultStatusSchema,
  })
  .strict();
export type ExecuteWorkflowToolResultType = z.infer<
  typeof ExecuteWorkflowToolResultSchema
>;

/**
 * `metadata` on agent `message` rows recorded by `investigation_wf_terminal_event_handler`.
 */
export const ExecuteWorkflowToolResultMetadataSchema = z
  .object({
    tool_name: z.literal(PendingApprovalToolName.EXECUTE_WORKFLOW),
    result: ExecuteWorkflowToolResultSchema,
  })
  .passthrough();
export type ExecuteWorkflowToolResultMetadataType = z.infer<
  typeof ExecuteWorkflowToolResultMetadataSchema
>;

/** Reference to a DP file that has been uploaded via the file API. */
export const DpFileArtifactSchema = z
  .object({
    kind: z.literal("dp_file"),
    name: z.string().min(1),
    mime_type: z.string().min(1),
    ref: z
      .object({
        file_id: z.string().uuid(),
      })
      .strict(),
  })
  .strict();

/** Artifact union for investigation-message metadata payloads. */
export const ArtifactRefSchema = z.discriminatedUnion("kind", [
  DpFileArtifactSchema,
]);
export type ArtifactRefType = z.infer<typeof ArtifactRefSchema>;

export const InvestigationMessageArtifactsMetadataSchema = z
  .object({
    artifacts: z.array(ArtifactRefSchema),
  })
  .strict();
export type InvestigationMessageArtifactsMetadataType = z.infer<
  typeof InvestigationMessageArtifactsMetadataSchema
>;

export const InvestigationMessageSchema = z
  .object({
    investigation_id: z.string().uuid(),
    role: z.nativeEnum(InvestigationMessageRole),
    kind: z.nativeEnum(InvestigationMessageKind),
    content: z.string(),
    metadata: z.record(z.unknown()).nullable(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type InvestigationMessageType = z.infer<
  typeof InvestigationMessageSchema
>;

export const MAX_INVESTIGATION_MESSAGE_LENGTH = 10_000;

export const MAX_INVESTIGATION_TITLE_LENGTH = 80;

export const InvestigationSchema = z
  .object({
    company_id: z.string().uuid(),
    user_id: z.string().uuid(),
    title: z.string().max(MAX_INVESTIGATION_TITLE_LENGTH).nullable(),
    status: z.nativeEnum(InvestigationStatus),
    archived: z.boolean(),
    runtime_session_id: z.string().min(33),
    progress: z.array(InvestigationProgressEventSchema),
    report: z.string().nullable(),
    error: InvestigationErrorSchema.nullable(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type InvestigationType = z.infer<typeof InvestigationSchema>;

export const InvestigationArchiveStatus = {
  ACTIVE: "active",
  ARCHIVED: "archived",
} as const;
export type InvestigationSearchStatusType =
  (typeof InvestigationArchiveStatus)[keyof typeof InvestigationArchiveStatus];

export const InvestigationListFiltersSchema = InvestigationSchema.pick({
  id: true,
  title: true,
})
  .extend({
    status: z.array(z.nativeEnum(InvestigationArchiveStatus)),
  })
  .partial();

export const InvestigationGetListRequestSchema = createGetListRequestSchema(
  InvestigationListFiltersSchema
);
export type InvestigationGetListRequestType = z.infer<
  typeof InvestigationGetListRequestSchema
>;
export type InvestigationGetListFiltersType = NonNullable<
  InvestigationGetListRequestType["filters"]
>;

/** Detail view shape: API GET may omit `messages` until conversation UI ships (F2). */
export type InvestigationDetailType = InvestigationType & {
  messages: InvestigationMessageType[];
};

export const CreateInvestigationBodySchema = z.object({
  content: z.string().min(1).max(MAX_INVESTIGATION_MESSAGE_LENGTH),
  metadata: InvestigationMessageArtifactsMetadataSchema.optional(),
});
export type CreateInvestigationBodyType = z.infer<
  typeof CreateInvestigationBodySchema
>;

export const CreateInvestigationResponseSchema = z.object({
  investigation_id: z.string().uuid(),
});
export type CreateInvestigationResponseType = z.infer<
  typeof CreateInvestigationResponseSchema
>;

export const UpdateInvestigationBodySchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1)
      .max(MAX_INVESTIGATION_TITLE_LENGTH)
      .optional(),
    archived: z.boolean().optional(),
  })
  .strict()
  .refine(
    (value) => value.title !== undefined || value.archived !== undefined,
    { message: "At least one of title or archived is required" }
  );
export type UpdateInvestigationBodyType = z.infer<
  typeof UpdateInvestigationBodySchema
>;

export const CreateInvestigationMessageBodySchema = z.discriminatedUnion(
  "kind",
  [
    z
      .object({
        kind: z.literal("message"),
        content: z.string().min(1).max(MAX_INVESTIGATION_MESSAGE_LENGTH),
        metadata: InvestigationMessageArtifactsMetadataSchema.optional(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("approval"),
        decision: z.nativeEnum(ApprovalDecision),
        content: z
          .string()
          .min(1)
          .max(MAX_INVESTIGATION_MESSAGE_LENGTH)
          .optional(),
      })
      .strict(),
  ]
);
export type CreateInvestigationMessageBodyType = z.infer<
  typeof CreateInvestigationMessageBodySchema
>;

export const CreateInvestigationMessageResponseSchema = z.object({
  investigation_id: z.string().uuid(),
});
export type CreateInvestigationMessageResponseType = z.infer<
  typeof CreateInvestigationMessageResponseSchema
>;

export const ListInvestigationsResponseSchema = z.object({
  page_size: z.number().min(0),
  page_number: z.number().min(1),
  total_entries: z.number().min(0),
  total_pages: z.number().min(0),
  data: z.array(InvestigationSchema),
});
export type ListInvestigationsResponseType = z.infer<
  typeof ListInvestigationsResponseSchema
>;

export const ListInvestigationMessagesResponseSchema = z.object({
  page_size: z.number().min(0),
  page_number: z.number().min(1),
  total_entries: z.number().min(0),
  total_pages: z.number().min(0),
  data: z.array(InvestigationMessageSchema),
});
export type ListInvestigationMessagesResponseType = z.infer<
  typeof ListInvestigationMessagesResponseSchema
>;
